import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort, Sort } from '@angular/material/sort';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { TableConfig } from './models/tableConfig.model';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { UntypedFormGroup } from '@angular/forms';
import { TableRowInfo } from './models/tableRowInfo.model';
import { FormlyFieldConfig } from '@ngx-formly/core';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: [ './table.component.scss' ],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0px' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      )
    ])
  ]
})
export class TableComponent implements OnChanges, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  @Input() public config!: TableConfig<any>;
  @Input() public enableCheckbox: boolean = false;
  @Input() public enableDragAndDrop = false;
  @Input() public pageSize: number = 10;
  @Input() public pageSizeOptions: number[] = [ 10, 20, 50 ];
  @Input() public cleanSelectList: boolean = false;
  @Input() public cleanSelectBoxList: boolean = false;
  @Input() public selectedList!: any[];
  @Input() public saveAll: boolean = false;

  @Output() private selectedRows: EventEmitter<TableRowInfo[]> =
    new EventEmitter<TableRowInfo[]>();
  @Output() private pageChange: EventEmitter<PageEvent> =
    new EventEmitter<PageEvent>();
  @Output() private dragAndDropChange: EventEmitter<CdkDragDrop<string[]>> =
    new EventEmitter<CdkDragDrop<string[]>>();
  @Output() private allSelected: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() private cellBoxSelect: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() private sortEvent: EventEmitter<Sort> = new EventEmitter<Sort>();
  @Output() private rowInfo: EventEmitter<any[]> = new EventEmitter<any[]>();

  public dataSource: MatTableDataSource<any> = new MatTableDataSource();
  public displayedColumns: Iterable<string> = [];
  public expandedElement: any | null;
  public form = new UntypedFormGroup({});
  public model: any = {};
  public fields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: '',
      fieldGroup: []
    }
  ];
  public allSelectedRows: any[] = [];
  public isAllSelect: boolean = false;

  private cellBoxSelectList: any = [];

  constructor() {
  }

  ngAfterViewInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.cleanSelectBoxList) {
      this.cellBoxSelectList = [];
      this.cellBoxSelect.emit(this.cellBoxSelectList);
    }
    if (this.cleanSelectList) {
      this.isAllSelect = false;
      this.allSelectedRows = [];
    }
    if (this.selectedList !== undefined) {
      this.allSelectedRows = this.selectedList;
    }
    if (this.enableCheckbox) {
      this.displayedColumns = [
        'select',
        ...this.config?.columns
          ?.filter(f => f.isDisplayed)
          .map(m => m.columnDef)
      ];
    } else {
      this.displayedColumns = this.config?.columns
        .filter(f => f.isDisplayed)
        .map(m => m.columnDef);
    }

    this.dataSource = new MatTableDataSource<any>(this.config?.dataSource);
    this.dataSource.paginator = this.paginator;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected(): any {
    return this.saveAll === true
      ? this.config.dataSourceLength === this.allSelectedRows.length &&
      this.isAllSelect
      : this.isAllSelect;
  }

  /** Selects all rows if they are not all selected; otherwise remove all rows from that page */
  masterToggle(): void {
    this.isAllSelect = !this.isAllSelect;
    this.allSelectedRows =
      this.saveAll && this.isAllSelect ? this.allSelectedRows : [];
    this.allSelected.emit(this.isAllSelect);
    this.selectedRows.emit(this.allSelectedRows);
  }

  // Select row and send it to parent component
  rowToggle(index: any, row: any): void {
    const pageFirstItemIndex =
      this.paginator?.pageIndex * this.paginator?.pageSize;
    const rowInfo: TableRowInfo = {
      index: pageFirstItemIndex + index,
      value: row
    };
    const existIndex = this.isSelected(row);
    if (existIndex !== -1) {
      this.allSelectedRows.splice(existIndex, 1);
    } else {
      this.allSelectedRows.push(rowInfo);
    }
    this.selectedRows.emit(this.allSelectedRows);
  }

  // Compare two value
  compareValues(a: number | string, b: number | string, isAsc: boolean): any {
    return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
  }

  // Send sort event to parent component
  sortData(sort: Sort): void {
    this.sortEvent.emit(sort);
  }

  // Update dataSource when page changed
  onChangePage(pageEvent: PageEvent): void {
    this.pageChange.emit(pageEvent);
  }

  // Drag and drop event
  onListDrop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(
      this.config?.dataSource,
      event.previousIndex,
      event.currentIndex
    );
    this.dataSource = new MatTableDataSource<Element>(this.config?.dataSource);
    this.dragAndDropChange.emit(event);
  }

  // Sync between pagers
  syncPagers(event: PageEvent): void {
    // this.paginator.pageIndex = event.pageIndex;
    // this.paginator.pageSize = event.pageSize;
    // this.paginator.page.emit(event);
  }

  // Show row detail in a expandeble row
  iconAction(action: any, row: any): void {
    if (action.showDetailIcon) {
      this.expandedElement = this.expandedElement === row ? null : row;
    }
    action.action(row);
  }

  // Show input to modify the data in the column
  showEditCell(column: any, row: any): void {
    this.rowInfo.emit(row);
    this.config.editColumnConfig?.forEach((config, index) => {
      if (
        this.config.editColumnConfig !== undefined &&
        config.key === column.columnDef
      ) {
        this.fields[0].fieldGroup = [ this.config.editColumnConfig[index] ];
      }
    });
    this.model[column.columnDef] = column.cell(row);
  }

  // Check if a row is selected, return -1 if the item does not exist
  isSelected(row: any): any {
    return this.allSelectedRows.findIndex(fi => fi.value.id === row.id);
  }

  isCellBoxSelect(row: any, columnName: any): any {
    return this.cellBoxSelectList.findIndex((item: any) => item.column === columnName && item.value.id === row.id);
  }

  cellBoxChange(row: any, columnName: any): void {
    const exist = this.isCellBoxSelect(row, columnName);
    if (exist === -1) {
      this.cellBoxSelectList.push({
        column: columnName,
        value: row
      });
    } else {
      this.cellBoxSelectList.splice(exist, 1);
    }
    this.cellBoxSelect.emit(this.cellBoxSelectList);
  }
}
